import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { adminApi, fmtTRY, fmtDate } from "@/lib/api";
import { Bell, CheckCircle2, Clock3, Trash2 } from "lucide-react";

const rowKey = (r) => `${r.quote_id}-${r.item_idx}`;

export default function PriceRequestsView({ PageHeader, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  // V17.16: seçilen satırlar yalnız bu listeden kaldırılır; teklif ve ürün yerinde kalır.
  const [checked, setChecked] = useState(new Set());
  const [removing, setRemoving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/price-requests");
      setRows(r.data || []);
      setChecked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fiyat bekleyenler alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changed = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const toggle = (key) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allChecked = rows.length > 0 && rows.every((r) => checked.has(rowKey(r)));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map(rowKey)));

  const removeSelected = async () => {
    const items = rows.filter((r) => checked.has(rowKey(r))).map((r) => ({ quote_id: r.quote_id, item_idx: r.item_idx }));
    if (!items.length) return;
    if (!window.confirm(`${items.length} satır Fiyat Bekleyenler listesinden kaldırılacak.\nTeklifler silinmez; ürün teklifte "fiyat bekliyor" olarak kalır.\n\nDevam edilsin mi?`)) return;
    setRemoving(true);
    try {
      const r = await adminApi.post("/admin/price-requests/dismiss", { items });
      toast.success(`${r.data?.dismissed || 0} satır listeden kaldırıldı.`);
      await changed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Satırlar kaldırılamadı.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Fiyat Bekleyenler"
        subtitle="Müşterinin fiyat istediği fiyatsız eşleşmiş ürünleri tamamlayın; müşteri notunu kontrol ederek doğru ürünü fiyatlandırın"
        actions={checked.size > 0 ? (
          <Button type="button" size="sm" onClick={removeSelected} disabled={removing} className="bg-red-600 hover:bg-red-700 text-white">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {removing ? "Kaldırılıyor…" : `${checked.size} Satırı Listeden Kaldır`}
          </Button>
        ) : null}
      />
      <div className="p-8">
        {loading ? (
          <div className="text-sm text-[#4B5563]">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <Card className="border-[#E2E8F0] p-12 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
            <div className="font-semibold text-[#1E3A5F]">Bekleyen fiyat yok</div>
            <p className="text-sm text-[#4B5563] mt-1">Fiyatsız ürün katalogda eşleşir; müşteri “Fiyat İste” dediğinde burada görünür.</p>
          </Card>
        ) : (
          <Card className="border-[#E2E8F0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F8FAFC]">
                    <Th className="w-10">
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 accent-[#DC2626]" title="Tümünü seç" />
                    </Th>
                    <Th>Tarih</Th><Th>Müşteri</Th><Th>Aranan</Th><Th>Eşleşen Ürün</Th>
                    <Th className="text-right">Mik.</Th><Th>Son Tedarikçi Bilgisi</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={rowKey(r)} className={`border-b border-[#F1F5F9] hover:bg-[#F8FAFC] ${checked.has(rowKey(r)) ? "bg-red-50/60" : ""}`}>
                      <td className="px-4 py-3 text-center">
                        <input type="checkbox" checked={checked.has(rowKey(r))} onChange={() => toggle(rowKey(r))} className="w-4 h-4 accent-[#DC2626]" />
                      </td>
                      <td className="px-4 py-3 text-xs text-[#4B5563] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#1F2937]">{r.customer?.company || r.customer?.name}</div>
                        <div className="text-xs text-[#6B7280]">{r.customer?.email || "E-posta yok"}</div>
                      </td>
                      <td className="px-4 py-3 text-[#4B5563]">
                        <div>{r.searched}</div>
                        {r.customer_note && (
                          <div className="mt-1 text-[11px] text-[#1E3A5F] bg-blue-50 border border-blue-100 rounded px-2 py-1">
                            <strong>Müşteri notu:</strong> {r.customer_note}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#1F2937]">{r.product_name}</div>
                        <div className="text-xs text-[#6B7280]">{r.brand_name} {r.product_code ? `· ${r.product_code}` : ""}</div>
                      </td>
                      <td className="px-4 py-3 text-right">{r.qty}</td>
                      <td className="px-4 py-3 text-xs">
                        {r.last_supplier_price ? (
                          <div>
                            <div className="font-medium">{r.last_supplier_price.supplier_name || "—"}</div>
                            <div className="text-[#6B7280]">{fmtTRY(r.last_supplier_price.net_price)} net</div>
                          </div>
                        ) : <span className="text-[#9CA3AF]">Kayıt yok</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" onClick={() => setSelected(r)} className="bg-[#F97316] hover:bg-[#EA580C] text-white">
                          Fiyat Gir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      <ApprovalDialog request={selected} onClose={() => setSelected(null)} onApproved={changed} />
    </div>
  );
}

function ApprovalDialog({ request, onClose, onApproved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    list_price: "", cash_discount: "", card_discount: "",
    supplier_name: "", supplier_list_price: "", supplier_discount: "", supplier_source: "", supplier_price_date: "",
    extra_feature: "", supplier_note: "", internal_note: "",
    save_to_catalog: true, learn_match: false,
  });

  useEffect(() => {
    if (!request) return;
    const last = request.last_supplier_price || {};
    setForm({
      list_price: "", cash_discount: "", card_discount: "",
      supplier_name: last.supplier_name || "",
      supplier_list_price: last.list_price ?? "",
      supplier_discount: last.discount ?? "",
      supplier_source: last.source || "",
      supplier_price_date: last.price_date ? String(last.price_date).slice(0,10) : "",
      extra_feature: last.extra_feature || "",
      supplier_note: "", internal_note: "",
      save_to_catalog: true, learn_match: false,
    });
  }, [request]);

  if (!request) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const approve = async () => {
    const price = Number(String(form.list_price).replace(",", "."));
    if (!(price > 0)) return toast.error("Geçerli bir liste fiyatı girin.");
    setSaving(true);
    try {
      const payload = {
        list_price: price,
        cash_discount: form.cash_discount === "" ? null : Number(String(form.cash_discount).replace(",", ".")),
        card_discount: form.card_discount === "" ? null : Number(String(form.card_discount).replace(",", ".")),
        supplier_name: form.supplier_name,
        supplier_list_price: form.supplier_list_price === "" ? null : Number(String(form.supplier_list_price).replace(",", ".")),
        supplier_discount: form.supplier_discount === "" ? null : Number(String(form.supplier_discount).replace(",", ".")),
        supplier_source: form.supplier_source,
        supplier_price_date: form.supplier_price_date || null,
        supplier_note: form.supplier_note,
        extra_feature: form.extra_feature,
        internal_note: form.internal_note,
        save_to_catalog: form.save_to_catalog,
        learn_match: form.learn_match,
      };
      const r = await adminApi.post(`/admin/price-requests/${request.quote_id}/${request.item_idx}/approve`, payload);
      if (r.data?.ready) {
        if (r.data?.email?.sent) toast.success("Fiyat onaylandı. Teklif müşteriye PDF ekli e-posta ile gönderildi.");
        else toast.success("Fiyat onaylandı ve teklif hazır. E-posta ayarını kontrol edin.");
      } else {
        toast.success("Fiyat onaylandı. Teklifte bekleyen başka kalemler var.");
      }
      onClose();
      onApproved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fiyat onaylanamadı.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A5F] flex items-center gap-2"><Bell className="w-4 h-4 text-[#F97316]" /> Fiyat Onayı</DialogTitle>
          <DialogDescription>{request.product_name} — {request.qty} adet</DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-xs">
          <div><strong>Müşteri satırı:</strong> {request.searched}</div>
          <div className="mt-1"><strong>Müşteri:</strong> {request.customer?.company} · {request.customer?.email}</div>
          {request.customer_note && <div className="mt-2 rounded bg-blue-50 border border-blue-100 px-2 py-1.5 text-[#1E3A5F]"><strong>Müşteri notu:</strong> {request.customer_note}</div>}
        </div>

        <section className="space-y-3">
          <h4 className="font-semibold text-sm text-[#1E3A5F]">Müşteriye uygulanacak fiyat</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Liste Fiyatı *"><Input value={form.list_price} onChange={set("list_price")} placeholder="0,00" /></Field>
            <Field label="Nakit İskonto %"><Input value={form.cash_discount} onChange={set("cash_discount")} placeholder="Marka varsayılanı" /></Field>
            <Field label="Kart İskonto %"><Input value={form.card_discount} onChange={set("card_discount")} placeholder="Marka varsayılanı" /></Field>
          </div>
        </section>

        <section className="space-y-3 border-t pt-4">
          <h4 className="font-semibold text-sm text-[#1E3A5F]">İç tedarikçi hafızası <Badge variant="outline" className="ml-2">Müşteri görmez</Badge></h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Firma"><Input value={form.supplier_name} onChange={set("supplier_name")} placeholder="Tedarikçi firma" /></Field>
            <Field label="Alınan Liste Fiyatı"><Input value={form.supplier_list_price} onChange={set("supplier_list_price")} placeholder="0,00" /></Field>
            <Field label="Alınan İskonto %"><Input value={form.supplier_discount} onChange={set("supplier_discount")} placeholder="0" /></Field>
            <Field label="Kaynak"><Input value={form.supplier_source} onChange={set("supplier_source")} placeholder="Telefon / WhatsApp / PDF / E-posta" /></Field>
            <Field label="Fiyat Tarihi"><Input type="date" value={form.supplier_price_date} onChange={set("supplier_price_date")} /></Field>
          </div>
          <Field label="Ürünün ekstra özelliği"><Input value={form.extra_feature} onChange={set("extra_feature")} placeholder="Örn. eski seri, özel ambalaj, 100 adet üzeri" /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Bu fiyat kaydına not"><Textarea value={form.supplier_note} onChange={set("supplier_note")} /></Field>
            <Field label="Ürünün kalıcı iç notu"><Textarea value={form.internal_note} onChange={set("internal_note")} /></Field>
          </div>
        </section>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.save_to_catalog} onChange={(e) => setForm(f => ({...f, save_to_catalog:e.target.checked}))} /> Bu fiyatı kataloğa kaydet</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.learn_match} onChange={(e) => setForm(f => ({...f, learn_match:e.target.checked}))} /> Bu eşleşmeden öğren</label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={approve} disabled={saving} className="bg-[#F97316] hover:bg-[#EA580C] text-white">
            <Clock3 className="w-4 h-4 mr-2" /> {saving ? "Onaylanıyor…" : "Fiyatı Onayla"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-[#4B5563]">{label}</Label>{children}</div>;
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[10px] uppercase tracking-[0.1em] font-semibold border-b border-[#E2E8F0] text-left text-[#4B5563] ${className}`}>{children}</th>
);
